/**
 * Search Overlay V2 - Header-integrated dropdown approach
 * 
 * Features:
 * - Hooks into existing header search input
 * - Shows dropdown panel below search bar
 * - Category sidebar with filters
 * - Bulk pricing display
 * - Add to cart functionality
 * - Toggle switch to enable/disable
 */

(function() {
  'use strict';
  
  // ============================================
  // CONFIGURATION & STATE
  // ============================================
  
  // =============================================
  // CODE TOGGLE: Set to true to use new search, false for old search
  // =============================================
  const USE_NEW_SEARCH = true;
  
  const CONFIG = {
    debounceMs: 10,
    minChars: 2,
    resultsPerPage: 10,
    maxResults: 100,
    enabled: USE_NEW_SEARCH
  };
  
  let state = {
    isOpen: false,
    isLoading: false,
    isLoadingMore: false,
    searchTerm: '',
    results: [],
    allResults: [],
    displayedCount: 0,
    dropdown: null,
    originalInput: null,
    debounceTimer: null
  };

  // Popular searches to show on init (randomly selected)
  const POPULAR_SEARCHES = [
    'a4 paper',
    'staedtler',
    'pens',
    'paper',
    'desk',
    'tape',
    'whiteboard',
    'sharpie',
    'avery',
    'labels'
  ];
  
  function getRandomPopularSearches(count = 6) {
    const shuffled = [...POPULAR_SEARCHES].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  }

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  
  function debounce(func, wait) {
    return function executedFunction(...args) {
      clearTimeout(state.debounceTimer);
      state.debounceTimer = setTimeout(() => func.apply(this, args), wait);
    };
  }
  
  function formatMoney(price) {
    // Shopify predictive search returns price as string in dollars (e.g., "15.00" or "1,159.00")
    // Remove commas before parsing to handle formatted prices
    const value = typeof price === 'string' ? parseFloat(price.replace(/,/g, '')) : price;
    if (isNaN(value)) return '$0.00';
    return '$' + value.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  
  function getResizedImageUrl(url, size) {
    if (!url) return '';
    if (url.includes('_' + size + '.') || url.includes('_' + size + 'x.')) {
      return url;
    }
    const match = url.match(/\.(jpg|jpeg|png|gif|webp)(\?v=\d+)?$/i);
    if (match) {
      return url.replace(match[0], '_' + size + match[0]);
    }
    return url;
  }

  function extractBulkPricing(product) {
    if (!product.tags) return null;
    
    const tags = Array.isArray(product.tags) ? product.tags : product.tags.split(',');
    const bdoTag = tags.find(tag => tag.trim().toLowerCase().startsWith('bdo|'));
    
    if (!bdoTag) return null;
    
    const tiers = [];
    const parts = bdoTag.split('|').slice(1);
    
    parts.forEach(part => {
      const match = part.match(/^(\d+):(\d+(?:\.\d+)?)/);
      if (match) {
        tiers.push({
          qty: parseInt(match[1]),
          discount: parseFloat(match[2])
        });
      }
    });
    
    if (tiers.length === 0) return null;
    
    // Sort by qty to get the best discount (highest qty tier)
    tiers.sort((a, b) => b.qty - a.qty);
    
    return tiers;
  }
  
  // ============================================
  // GST TOGGLE FUNCTIONS
  // ============================================
  
  /**
   * Get current GST preference from cookie
   * Cookie name: pricetype_gst
   * Values: 'inc_gst' or 'ex_gst'
   */
  function getGstPreference() {
    const match = document.cookie.match(/pricetype_gst=([^;]+)/);
    return match ? match[1] : 'inc_gst'; // Default to inc_gst
  }
  
  /**
   * Apply GST toggle to a single card or container
   * Shows/hides pt-inc_gst and pt-ex_gst elements based on cookie
   */
  function applyGstToggleToCard(container) {
    const pref = getGstPreference();
    const isIncGst = pref === 'inc_gst';
    
    // Find all GST-toggled elements in this container
    const incElements = container.querySelectorAll('.pt-inc_gst');
    const exElements = container.querySelectorAll('.pt-ex_gst');
    
    incElements.forEach(el => {
      if (isIncGst) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    });
    
    exElements.forEach(el => {
      if (isIncGst) {
        el.classList.add('hidden');
      } else {
        el.classList.remove('hidden');
      }
    });
  }
  
  /**
   * Apply GST toggle to the entire dropdown
   */
  function applyGstToggleToDropdown() {
    if (!state.dropdown) return;
    applyGstToggleToCard(state.dropdown);
  }
  
async function searchCollections(searchTerm) {
    const term = searchTerm.toLowerCase().trim();
    if (!term || term.length < 2) return [];
    
    try {
      // Use Shopify's Predictive Search API to search collections
      const url = `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=collection&resources[limit]=6`;
      const response = await fetch(url);
      if (!response.ok) return [];
      
      const data = await response.json();
      const collections = data.resources?.results?.collections || [];
      
      return collections.map(c => ({
        name: c.title,
        url: c.url
      }));
    } catch (e) {
      return [];
    }
  }
  
  // ============================================
  // SEARCH API
  // ============================================
  
  async function performSearch(query) {
    if (!query || query.length < CONFIG.minChars) {
      return { products: [] };
    }
    
    try {
      // Use the regular search API with JSON template for more results (up to 50)
      // The predictive search API is limited to 10 products max
      const url = `/search?view=json&q=${encodeURIComponent(query)}*&type=product`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Search request failed');
      
      const text = await response.text();
      
      // Try to parse as JSON
      try {
        const data = JSON.parse(text);
        if (data.products && Array.isArray(data.products)) {
          return { products: data.products };
        }
      } catch (parseError) {
        // Fall back to Predictive Search API
      }
      
      // Fallback: Use Predictive Search API (limited to 10 results)
      const predictiveUrl = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=hide`;
      const predictiveResponse = await fetch(predictiveUrl);
      if (!predictiveResponse.ok) throw new Error('Predictive search request failed');
      
      const predictiveData = await predictiveResponse.json();
      return {
        products: predictiveData.resources?.results?.products || []
      };
    } catch (error) {
      return { products: [] };
    }
  }
  
  // ============================================
  // RENDERING
  // ============================================
  
  function renderProductCard(product) {
    const template = document.getElementById('search-v2-product-template');
    if (!template) {
      return null;
    }
    
    const card = template.content.cloneNode(true);
    const cardEl = card.querySelector('[data-product-card]');
    
    // Product links (image + title)
    const linkImage = card.querySelector('[data-product-link]');
    const linkTitle = card.querySelector('[data-product-link-title]');
    const image = card.querySelector('[data-product-image]');
    
    if (linkImage) linkImage.href = product.url;
    if (linkTitle) {
      linkTitle.href = product.url;
      linkTitle.textContent = product.title;
    }
    
    // Image - handle both full URL from JSON template and partial URL from Predictive Search
    if (image && product.image) {
      // If image URL already contains width parameter or is a full CDN URL, use as-is
      if (product.image.includes('width=') || product.image.includes('_260x')) {
        image.src = product.image;
      } else {
        image.src = getResizedImageUrl(product.image, '260x');
      }
      image.alt = product.title;
    }
    
    // Pricing - price comes as string in dollars (e.g., "15.00") from both APIs
    // Render both inc GST and ex GST versions for cookie-based toggle
    const priceIncEl = card.querySelector('[data-product-price-inc]');
    const priceExEl = card.querySelector('[data-product-price-ex]');
    const compareIncEl = card.querySelector('[data-product-compare-inc]');
    const compareExEl = card.querySelector('[data-product-compare-ex]');
    
    // Use the price from API - template now sends price_max when price_varies is true
    // Remove commas from price string before parsing (e.g., "1,159.00" -> "1159.00")
    let price = parseFloat(String(product.price).replace(/,/g, '')) || 0;
    
    const priceExGst = price / 1.15; // Ex GST price
    const compareAtPriceStr = product.compare_at_price_max || product.compare_at_price || '0';
    const compareAtPrice = parseFloat(String(compareAtPriceStr).replace(/,/g, '')) || 0;
    const compareAtPriceExGst = compareAtPrice ? compareAtPrice / 1.15 : null;
    
    // Check if product is in a promotion collection (contains '-on-sale' in handle)
    const collections = product.collections || [];
    const hasPromotion = collections.some(handle => handle && handle.includes('-on-sale'));
    const hasPricefreeze = (product.tags || []).some(tag => tag === 'pricefreeze');
    const isOnSale = hasPromotion && !hasPricefreeze;
    
    // Set inc GST price
    if (priceIncEl) priceIncEl.textContent = formatMoney(price);
    // Set ex GST price
    if (priceExEl) priceExEl.textContent = formatMoney(priceExGst);
    
    // Only show RRP/compare price and badge for actual promotions (on-sale collections)
    if (isOnSale && compareAtPrice && compareAtPrice > price) {
      // Show compare prices (both inc and ex GST)
      if (compareIncEl) {
        compareIncEl.textContent = formatMoney(compareAtPrice);
        compareIncEl.style.display = '';
      }
      if (compareExEl) {
        compareExEl.textContent = formatMoney(compareAtPriceExGst);
        compareExEl.style.display = '';
      }
      
      // Add sale class and show badge
      if (cardEl) cardEl.classList.add('search-v2-product--on-sale');
      
      const badge = card.querySelector('[data-product-badge]');
      if (badge) {
        const savingsPercent = Math.round((1 - price / compareAtPrice) * 100);
        if (savingsPercent >= 10) {
          badge.textContent = `${savingsPercent}% OFF`;
        } else {
          badge.textContent = 'SALE';
        }
        badge.style.display = '';
      }
    }
    
    // Bulk pricing - render both inc and ex GST versions
    const bulkPricing = extractBulkPricing(product);
    const bulkIncEl = card.querySelector('[data-product-bulk-inc]');
    const bulkExEl = card.querySelector('[data-product-bulk-ex]');
    const bulkPriceIncEl = card.querySelector('[data-bulk-price-inc]');
    const bulkPriceExEl = card.querySelector('[data-bulk-price-ex]');
    
    if (bulkPricing && bulkPricing.length > 0) {
      const bestTier = bulkPricing[0]; // Already sorted by highest qty
      const discountedPrice = price * (1 - bestTier.discount / 100);
      const discountedPriceExGst = Math.round(discountedPrice / 1.15);
      
      if (bulkPriceIncEl && bulkIncEl) {
        bulkPriceIncEl.textContent = formatMoney(discountedPrice);
        bulkIncEl.style.display = '';
      }
      if (bulkPriceExEl && bulkExEl) {
        bulkPriceExEl.textContent = formatMoney(discountedPriceExGst);
        bulkExEl.style.display = '';
      }
    }
    
    // Apply current GST toggle state to this card
    applyGstToggleToCard(card);
    
    // Variant ID for add to cart
    const addBtn = card.querySelector('[data-add-to-cart]');
    if (addBtn) {
      addBtn.dataset.productUrl = product.url;
      addBtn.dataset.productHandle = product.handle || product.url.split('/products/')[1]?.split('?')[0];
    }
    
    return card;
  }
  
  function renderProducts(products, searchTerm, append = false) {
    const grid = state.dropdown?.querySelector('[data-search-v2-grid]');
    if (!grid) return;
    
    // If not appending, clear and store all results
    if (!append) {
      grid.innerHTML = '';
      state.allResults = products;
      state.displayedCount = 0;
    }
    
    // Determine which products to render
    const startIndex = state.displayedCount;
    const endIndex = Math.min(startIndex + CONFIG.resultsPerPage, state.allResults.length);
    const productsToRender = state.allResults.slice(startIndex, endIndex);
    
    // Render each product
    productsToRender.forEach(product => {
      const card = renderProductCard(product);
      if (card) grid.appendChild(card);
    });
    
    state.displayedCount = endIndex;
    
    // Update counts
    const statusEl = state.dropdown?.querySelector('[data-search-v2-status]');
    const headerEl = state.dropdown?.querySelector('[data-search-v2-header]');
    const viewAllEl = state.dropdown?.querySelector('[data-search-v2-view-all]');
    
    if (statusEl) statusEl.innerHTML = `<span data-search-v2-count>${state.allResults.length}</span> results`;
    if (headerEl) headerEl.classList.add('visible');
    if (viewAllEl) viewAllEl.href = `/search?q=${encodeURIComponent(searchTerm)}`;
    
    // Update category suggestions based on search term
    if (!append) {
      updateCategorySuggestions(searchTerm);
    }
    
    // Show/hide "Load More" button
    updateLoadMoreButton();
  }
  
  function updateLoadMoreButton() {
    let loadMoreContainer = state.dropdown?.querySelector('[data-search-v2-load-more]');
    
    // Create load more container if it doesn't exist
    if (!loadMoreContainer) {
      const resultsContainer = state.dropdown?.querySelector('[data-search-v2-results]');
      if (resultsContainer) {
        loadMoreContainer = document.createElement('div');
        loadMoreContainer.className = 'search-v2-load-more';
        loadMoreContainer.setAttribute('data-search-v2-load-more', '');
        loadMoreContainer.innerHTML = `
          <button type="button" class="search-v2-load-more-btn" data-load-more-btn>
            Load More Results
          </button>
          <br>
          <span class="search-v2-load-more-count" data-load-more-count></span>
          <span class="search-v2-load-more-divider"> · </span>
          <a href="#" class="search-v2-view-all" data-view-all-link>View all →</a>
        `;
        resultsContainer.appendChild(loadMoreContainer);
        
        // Attach click handler
        const btn = loadMoreContainer.querySelector('[data-load-more-btn]');
        if (btn) {
          btn.addEventListener('click', handleLoadMore);
        }
      }
    }
    
    if (loadMoreContainer) {
      const hasMore = state.displayedCount < state.allResults.length;
      loadMoreContainer.style.display = hasMore ? 'block' : 'none';
      
      // Update count text and view all link
      const countEl = loadMoreContainer.querySelector('[data-load-more-count]');
      if (countEl) {
        countEl.textContent = `Showing ${state.displayedCount} of ${state.allResults.length} results`;
      }
      
      // Update view all link
      const viewAllLink = loadMoreContainer.querySelector('[data-view-all-link]');
      if (viewAllLink && state.searchTerm) {
        viewAllLink.href = `/search?q=${encodeURIComponent(state.searchTerm)}`;
      }
    }
  }
  
  function handleLoadMore() {
    if (state.isLoadingMore) return;
    
    state.isLoadingMore = true;
    const btn = state.dropdown?.querySelector('[data-load-more-btn]');
    if (btn) {
      btn.textContent = 'Loading...';
      btn.disabled = true;
    }
    
    // Small delay for visual feedback
    setTimeout(() => {
      renderProducts([], state.searchTerm, true);
      state.isLoadingMore = false;
      
      if (btn) {
        btn.textContent = 'Load More Results';
        btn.disabled = false;
      }
    }, 100);
  }
  
  function showPopularSearches() {
    const categoryContainer = state.dropdown?.querySelector('[data-search-v2-categories]');
    const headerEl = state.dropdown?.querySelector('[data-search-v2-header]');
    const statusEl = state.dropdown?.querySelector('[data-search-v2-status]');
    
    if (!categoryContainer) return;
    
    // Get random popular searches
    const popular = getRandomPopularSearches(6);
    
    // Build clickable buttons that trigger search
    const linksHtml = popular.map(term => 
      `<button type="button" class="search-v2-category-link search-v2-popular-btn" data-popular-search="${term}">${term}</button>`
    ).join('');
    
    categoryContainer.innerHTML = linksHtml;
    
    // Update header to show "Popular searches" instead of results count
    if (statusEl) statusEl.textContent = 'Popular searches';
    if (headerEl) headerEl.classList.add('visible');
    
    // Attach click handlers
    categoryContainer.querySelectorAll('[data-popular-search]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        try {
          const term = btn.dataset.popularSearch;
          const input = state.dropdown?.querySelector('[data-search-v2-input]');
          if (input && term) {
            input.value = term;
            // Trigger search directly
            showState('loading');
            state.searchTerm = term;
            const results = await performSearch(term);
            if (results.products.length === 0) {
              showState('no-results');
            } else {
              renderProducts(results.products, term);
              showState('results');
            }
          }
        } catch (err) {
          // Silently handle popular search errors
        }
      });
    });
  }
  
  async function updateCategorySuggestions(searchTerm) {
    const categoryContainer = state.dropdown?.querySelector('[data-search-v2-categories]');
    if (!categoryContainer) return;
    
    const matches = await searchCollections(searchTerm);
    
    // Clear if no matches
    if (matches.length === 0) {
      categoryContainer.innerHTML = '';
      return;
    }
    
    // Build links for matching collections
    const linksHtml = matches.map(match => 
      `<a href="${match.url}" class="search-v2-category-link">${match.name}</a>`
    ).join('');
    
    categoryContainer.innerHTML = linksHtml;
  }

  function showState(stateName) {
    const states = ['loading', 'results', 'no-results'];
    states.forEach(s => {
      const el = state.dropdown?.querySelector(`[data-search-v2-${s}]`);
      if (el) {
        if (s === stateName) {
          el.style.display = '';
          el.classList.add('visible');
        } else {
          el.style.display = 'none';
          el.classList.remove('visible');
        }
      }
    });
    
    // Show/hide header based on state
    const header = state.dropdown?.querySelector('[data-search-v2-header]');
    if (header) {
      if (stateName === 'results') {
        header.classList.add('visible');
      } else {
        header.classList.remove('visible');
      }
    }
  }
  
  // ============================================
  // DROPDOWN MANAGEMENT
  // ============================================
  
  function createDropdown() {
    const template = document.getElementById('search-v2-dropdown-template');
    if (!template) {
      return null;
    }
    
    const dropdown = template.content.cloneNode(true).firstElementChild;
    return dropdown;
  }
  
  function openDropdown() {
    if (state.isOpen || !CONFIG.enabled) return;
    
    // Create dropdown if it doesn't exist
    if (!state.dropdown) {
      state.dropdown = createDropdown();
      if (!state.dropdown) return;
      
      // Append to body (fixed position, full width at top)
      document.body.appendChild(state.dropdown);
      
      // Attach event listeners to the dropdown
      attachDropdownListeners();
    }
    
    state.isOpen = true;
    document.body.classList.add('search-overlay-v2-active');
    
    // Apply GST toggle state to dropdown elements
    applyGstToggleToDropdown();
    
    // Focus the overlay's search input
    const overlayInput = state.dropdown.querySelector('[data-search-v2-input]');
    if (overlayInput) {
      // Copy current search term if any
      if (state.originalInput && state.originalInput.value) {
        overlayInput.value = state.originalInput.value;
      }
      setTimeout(() => overlayInput.focus(), 50);
    }
    
    // Hide all content states initially (just show categories)
    showState('');
    
    // Show popular searches on init
    showPopularSearches();
  }
  
  function closeDropdown() {
    if (!state.isOpen) return;
    
    state.isOpen = false;
    document.body.classList.remove('search-overlay-v2-active');
    
    // Clear results
    if (state.dropdown) {
      const grid = state.dropdown.querySelector('[data-search-v2-grid]');
      if (grid) grid.innerHTML = '';
    }
    
    state.searchTerm = '';
  }
  
  function attachDropdownListeners() {
    if (!state.dropdown) return;
    
    // Get the overlay's own input
    const overlayInput = state.dropdown.querySelector('[data-search-v2-input]');
    
    // Close buttons (multiple - header X and footer text)
    const closeBtns = state.dropdown.querySelectorAll('[data-search-v2-close]');
    closeBtns.forEach(closeBtn => {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeDropdown();
      });
    });
    
    // Listen to the overlay's input for searches
    if (overlayInput) {
      overlayInput.addEventListener('input', (e) => {
        handleSearch(e.target.value.trim());
      });
      
      // Handle enter key to submit full search
      overlayInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && overlayInput.value.trim()) {
          window.location.href = `/search?type=product&q=${encodeURIComponent(overlayInput.value.trim())}`;
        }
        if (e.key === 'Escape') {
          closeDropdown();
        }
      });
    }
    
    // Quick search buttons
    state.dropdown.querySelectorAll('[data-quick-search]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const term = btn.dataset.quickSearch;
        if (overlayInput && term) {
          overlayInput.value = term;
          handleSearch(term);
        }
      });
    });
    
    // Add to cart buttons
    state.dropdown.addEventListener('click', async (e) => {
      const addBtn = e.target.closest('[data-add-to-cart]');
      if (!addBtn) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      // Update button state immediately - show spinner
      const originalText = addBtn.textContent;
      addBtn.innerHTML = '<span class="search-v2-spinner"></span>';
      addBtn.disabled = true;
      addBtn.classList.add('search-v2-product-atc--loading');
      
      try {
        // Get the product handle and fetch variant ID
        const productHandle = addBtn.dataset.productHandle;
        if (!productHandle) {
          throw new Error('No product handle');
        }
        
        // Fetch product JSON to get first available variant
        const productRes = await fetch(`/products/${productHandle}.js`);
        if (!productRes.ok) throw new Error('Failed to fetch product');
        
        const productData = await productRes.json();
        
        // Find first available variant
        const availableVariant = productData.variants.find(v => v.available) || productData.variants[0];
        if (!availableVariant) {
          throw new Error('No variant available');
        }
        
        // Add to cart
        await addToCart(availableVariant.id, 1);
        
        addBtn.textContent = 'Added!';
        addBtn.classList.remove('search-v2-product-atc--loading');
        addBtn.classList.add('search-v2-product-atc--success');
        
        // Update cart count
        updateCartCount();
        
        setTimeout(() => {
          addBtn.textContent = originalText;
          addBtn.disabled = false;
          addBtn.classList.remove('search-v2-product-atc--success');
          addBtn.classList.remove('search-v2-product-atc--loading');
        }, 1500);
      } catch (error) {
        addBtn.textContent = 'Error';
        setTimeout(() => {
          addBtn.textContent = originalText;
          addBtn.disabled = false;
        }, 1500);
      }
    });
  }
  
  async function addToCart(variantId, quantity) {
    const formData = {
      id: variantId,
      quantity: quantity
    };
    
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });
    
    if (!response.ok) {
      throw new Error('Add to cart failed');
    }
    
    return response.json();
  }
  
  function updateCartCount() {
    // Try to update the cart count in the header
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        // Try different selectors that themes commonly use
        const countSelectors = [
          '[data-cart-count]',
          '.cart-count',
          '.site-header-cart-count',
          '.header-cart-count',
          '#CartCount',
          '[data-header-cart-count]'
        ];
        
        countSelectors.forEach(selector => {
          const el = document.querySelector(selector);
          if (el) {
            el.textContent = cart.item_count;
            // Also update the data attribute that the theme uses
            if (el.hasAttribute('data-header-cart-count')) {
              el.setAttribute('data-header-cart-count', cart.item_count);
              el.classList.toggle('visible', cart.item_count > 0);
            }
          }
        });
        
        // Trigger jQuery event for Empire theme compatibility
        // The theme expects: $header.trigger('cartcount:update', { response: cart })
        if (typeof $ !== 'undefined' || typeof jQuery !== 'undefined') {
          const jq = $ || jQuery;
          const $header = jq('[data-section-type="static-header"]');
          if ($header.length) {
            $header.trigger('cartcount:update', { response: cart });
          }
        }
        
        // Also trigger custom event for other scripts
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      })
      .catch(() => {});
  }
  
  // ============================================
  // INPUT HANDLING
  // ============================================
  
  const handleSearch = debounce(async (term) => {
    if (!state.isOpen) return;
    
    if (!term || term.length < CONFIG.minChars) {
      showState('');
      showPopularSearches();
      return;
    }
    
    showState('loading');
    state.searchTerm = term;
    
    const results = await performSearch(term);
    
    // Check if search term hasn't changed during the request
    if (term !== state.searchTerm) return;
    
    if (results.products.length === 0) {
      showState('no-results');
    } else {
      showState('results');
      renderProducts(results.products, term);
    }
  }, CONFIG.debounceMs);
  
  function attachInputListeners(input) {
    if (!input) return;
    
    state.originalInput = input;
    
    // Hide ALL old flydowns permanently (but keep them in DOM so old code doesn't crash)
    document.querySelectorAll('[data-live-search-flydown], .search-flydown').forEach(el => {
      el.style.cssText = 'display: none !important; visibility: hidden !important; height: 0 !important; overflow: hidden !important;';
    });
    
    // Clone the input to remove all old event listeners
    const newInput = input.cloneNode(true);
    input.parentNode.replaceChild(newInput, input);
    input = newInput;
    state.originalInput = newInput;
    
    // Intercept the search button so the old empire.js handler doesn't fire
    const searchButton = input.closest('form')?.querySelector('[data-live-search-submit]');
    if (searchButton) {
      // Use capturing phase to beat the old jQuery handler
      searchButton.addEventListener('click', (e) => {
        if (!CONFIG.enabled) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        openDropdown();
        // Focus the overlay input
        setTimeout(() => {
          const overlayInput = state.dropdown?.querySelector('[data-search-v2-input]');
          if (overlayInput) overlayInput.focus();
        }, 50);
      }, true);
    }
    
    // Focus handler
    input.addEventListener('focus', () => {
      if (CONFIG.enabled) {
        openDropdown();
      }
    });
    
    // Input handler
    input.addEventListener('input', (e) => {
      if (!CONFIG.enabled || !state.isOpen) return;
      
      // Prevent old search from triggering
      e.stopPropagation();
      
      handleSearch(e.target.value.trim());
    });
    
    // Prevent form submission when dropdown is open
    const form = input.closest('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        if (CONFIG.enabled && state.isOpen && state.searchTerm) {
          // Allow form submission for full search results page
          closeDropdown();
        }
      });
    }
    
    // Handle escape key
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && state.isOpen) {
        e.preventDefault();
        closeDropdown();
        input.blur();
      }
    });
  }
  
  // ============================================
  // BACKDROP HANDLING
  // ============================================
  
  function attachBackdropListener() {
    const backdrop = document.querySelector('[data-search-v2-backdrop]');
    if (backdrop) {
      backdrop.addEventListener('click', () => {
        closeDropdown();
        if (state.originalInput) {
          state.originalInput.blur();
        }
      });
    }
  }
  
  // ============================================
  // TOGGLE (Code-controlled)
  // ============================================
  
  function initToggle() {
    // Toggle is now controlled via USE_NEW_SEARCH constant at the top of this file
  }
  
  // ============================================
  // INITIALIZATION
  // ============================================
  
  function init() {
    // Find search input - try multiple selectors
    const inputSelectors = [
      '[data-live-search-input]',
      '.site-header-search input[type="search"]',
      '.header-search input[type="search"]',
      '#search input[type="search"]',
      'input[name="q"][type="search"]'
    ];
    
    let searchInput = null;
    for (const selector of inputSelectors) {
      searchInput = document.querySelector(selector);
      if (searchInput) break;
    }
    
    if (searchInput) {
      attachInputListeners(searchInput);
    }
    
    // Set up backdrop
    attachBackdropListener();
    
    // Set up toggle
    initToggle();
  }
  
  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
})();
